/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import {
    IPriceComponentResources,
    ISwatchItem,
    //mmx-feature: Add to cart --start
    /*
    PriceComponent,
    */
    //mmx-feature: Add to cart --end
    ProductComponentSwatchComponent,
    RatingComponent
} from '@msdyn365-commerce/components';
//mmx-feature: Add to cart --start
import { AddToCartComponent } from './addtocart.component'
import { PriceComponent } from './price.component';
//mmx-feature: Add to cart --end
import {
    getCatalogId,
    //mmx-feature: Add to cart --start
    getUrlSync,
    //mmx-feature: Add to cart --end
    IAny,
    IComponent,
    IComponentProps,
    ICoreContext,
    IGeneric,
    IGridSettings,
    IImageData,
    IImageSettings,
    Image,
    msdyn365Commerce
} from '@msdyn365-commerce/core';
import {
    AttributeSwatch,
    AttributeValue,
    //mmx-feature: Add to cart --start
    ProductAvailableQuantity,
    //mmx-feature: Add to cart --end
    ProductDimension,
    ProductPrice,
    ProductSearchResult,
    ProductType,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    convertDimensionTypeToProductDimensionType,
    Dictionary,
    DimensionTypes,
    generateImageUrl,
    getProductPageUrlSync,
    IDimensionsApp,
    StringExtensions,
    validateCatalogId
} from '@msdyn365-commerce-modules/retail-actions';
import {
    format,
    getPayloadObject,
    getTelemetryAttributes,
    ITelemetryContent,
    onTelemetryClick
} from '@msdyn365-commerce-modules/utilities';
import React, { useState } from 'react';
//mmx-feature: --start
import { MailInRebateComponent } from '../../../../components/product/mail-in-rebate/mail-in-rebate';
import { getBrand, getProductName, IProductComponentAddToCartResources } from '../../../../utilities/product/utils';
import { IRebateFullLine } from '../../../../actions/DataServiceEntities.g';
import { _getFormattedPriceString, getValueFromExtensionProperties } from '../../../../utilities/utils';
//mmx-feature: --end

//mmx-feature: Add to cart --start
/*
export interface IProductComponentProps extends IComponentProps<{ product?: ProductSearchResult; productPrice?: ProductPrice }> {
*/
export interface IProductComponentProps
    extends IComponentProps<{
        product?: ProductSearchResult;
        productPrice?: ProductPrice;
        productDefaultVariant?: SimpleProduct;
        productAvailability?: ProductAvailableQuantity;
        productRebates?: IRebateFullLine[];
    }> {
    addToCartResources?: IProductComponentAddToCartResources;
    freeShippingLabel?: React.ReactNode;
    stockStatusLabel?: React.ReactNode;
    //mmx-feature: Add to cart --end
    className?: string;
    imageSettings?: IImageSettings;
    savingsText?: string;
    freePriceText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    ratingAriaLabel?: string;
    ratingCountAriaLabel?: string;
    allowBack?: boolean;
    telemetryContent?: ITelemetryContent;
    quickViewButton?: React.ReactNode;
    productComparisonButton?: React.ReactNode;
    isEnabledProductDescription?: boolean;
    isPriceMinMaxEnabled?: boolean;
    priceResources?: IPriceComponentResources;
    inventoryLabel?: string;
    dimensionAvailabilities?: IProductsDimensionsAvailabilities[];
    swatchItemAriaLabel?: string;
    //mmx-feature --start
    displayBasicAvailabilityOnly?: boolean;
    //mmx-feature --end
}

export interface IProductComponent extends IComponent<IProductComponentProps> {}

const PriceComponentActions = {};

/**
 * Gets the product page url from the default swatch selected.
 * @param  productData - Product card to be rendered.
 * @returns The default color swatch selected if any.
 */
function getDefaultSwatchSelected(coreContext: ICoreContext, productData?: ProductSearchResult): AttributeSwatch | null {
    if (!productData || !productData.AttributeValues) {
        return null;
    }

    const siteContext = coreContext as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    const attributeSwatches = productData.AttributeValues.find(
        attributeValue => attributeValue.KeyName?.toLocaleLowerCase() === dimensionToPreSelectInProductCard
    )?.Swatches;

    if (!ArrayExtensions.hasElements(attributeSwatches)) {
        return null;
    }

    const defaultSwatch = attributeSwatches.find(item => item.IsDefault === true) ?? attributeSwatches[0];
    return defaultSwatch;
}

/**
 * Gets the product image from the default swatch selected.
 * @param  coreContext - Context of the module using the component.
 * @param  productData - Product card to be rendered.
 * @returns The product card image url.
 */
function getProductImageUrlFromDefaultSwatch(coreContext: ICoreContext, productData?: ProductSearchResult): string | undefined {
    const defaultSwatch = getDefaultSwatchSelected(coreContext, productData);
    const swatchProductImageUrls = defaultSwatch?.ProductImageUrls;
    if (!ArrayExtensions.hasElements(swatchProductImageUrls)) {
        return productData?.PrimaryImageUrl;
    }

    return generateImageUrl(swatchProductImageUrls[0], coreContext.request.apiSettings);
}

/**
 * Updates the product url link to product details page.
 * @param  productDetailsPageUrl - Product page url.
 * @param  coreContext - Context of the module using the component.
 * @param  queryString - Querystring to be added to the URL.
 * @returns The update product page url.
 */
function updateProductUrl(productDetailsPageUrl: string, coreContext: ICoreContext, queryString: string): string {
    const sourceUrl = new URL(productDetailsPageUrl, coreContext.request.apiSettings.baseUrl);
    if (sourceUrl.search) {
        sourceUrl.search += `&${queryString}`;
    } else {
        sourceUrl.search += queryString;
    }

    const updatedUrl = new URL(sourceUrl.href);
    return updatedUrl.pathname + sourceUrl.search;
}

/**
 * Gets the react node for product unit of measure display.
 * @param  unitOfMeasure - DefaultUnitOfMeasure property from product.
 * @returns The node representing markup for unit of measure component.
 */
function renderProductUnitOfMeasure(unitOfMeasure?: string): JSX.Element | null {
    if (!unitOfMeasure) {
        return null;
    }
    return (
        <div className='msc-product__unit-of-measure'>
            <span>{unitOfMeasure}</span>
        </div>
    );
}

/**
 * Gets the react node for product availability.
 * @param inventoryAvailabilityLabel - The product information.
 * @returns The node representing markup for product availability.
 */
function renderProductAvailability(inventoryAvailabilityLabel: string | undefined): JSX.Element | null {
    if (!inventoryAvailabilityLabel || inventoryAvailabilityLabel === '') {
        return null;
    }

    return (
        <div className='msc-product__availability'>
            <span>{inventoryAvailabilityLabel}</span>
        </div>
    );
}

/**
 * Renders product comparison button similar to the quick view button.
 * @param productComparisonButton - React element of the button.
 * @param product - Current product info.
 * @param catalogId - Current catalog.
 * @returns React element for the specific product.
 */
function renderProductComparisonButton(
    productComparisonButton: React.ReactNode,
    product: ProductSearchResult,
    catalogId: number
): JSX.Element | undefined {
    validateCatalogId(catalogId);
    return React.cloneElement(productComparisonButton as React.ReactElement, { product, catalogId });
}

//mmx-feature: Sale image --start
function renderProductSaleImage( context: ICoreContext, product: ProductSearchResult, productPrice?: ProductPrice, priceRangeFeature?: boolean ): JSX.Element | null{
    const isPriceMinMaxEnabled = priceRangeFeature ?? false;

    const price: ProductPrice = productPrice ?? {
        BasePrice: product.BasePrice,
        AdjustedPrice: product.BasePrice,
        CustomerContextualPrice: product.Price,
        MaxVariantPrice: product.MaxVariantPrice ? product.MaxVariantPrice : product.Price,
        MinVariantPrice: product.MinVariantPrice ? product.MinVariantPrice : product.Price
    };
    // @ts-ignore
    if (!showStrikethroughPricing(product, price, isPriceMinMaxEnabled)) {
        return null;
    }

    const initialPrice = getOriginalPrice(price);
    const currentPrice = getActivePrice(product, price, isPriceMinMaxEnabled);
    const saleAmount = initialPrice - currentPrice;

    const saleTile: React.CSSProperties | undefined = {
        //@ts-ignore
        backgroundImage: context.app.config.productComponentSaleImageURL
            //@ts-ignore
            ? `url(${context.app.config.productComponentSaleImageURL})`
            : undefined
    };

    //like in price component, this is temporary solution
    const priceText = _getFormattedPriceString(saleAmount, context, undefined, true);

    return (
        <div className='msc-product__savings' style={saleTile}>
            <span>Save</span>
            <span>{priceText}</span>
        </div>
    );
}

const showStrikethroughPricing = (product: ProductSearchResult, productPrice: ProductPrice, priceRangeFeature: boolean): boolean => {
    const originalSalesPrice = getOriginalPrice(productPrice);
    const activeSalesPrice = showPriceRangeForMasterProduct(product, productPrice, priceRangeFeature)
        ? productPrice.MinVariantPrice
        : productPrice.CustomerContextualPrice;

    if (activeSalesPrice && originalSalesPrice) {
        return activeSalesPrice < originalSalesPrice;
    }

    return false;
};

const getOriginalPrice = (price: ProductPrice): number => {
    return price.AdjustedPrice ?? price.TradeAgreementPrice ?? price.BasePrice ?? 0;
};
const showPriceRangeForMasterProduct = (product: ProductSearchResult, price: ProductPrice, priceRangeFeature: boolean): boolean => {
    return (
        priceRangeFeature &&
        (product.IsMasterProduct ?? false) &&
        price.MinVariantPrice !== undefined &&
        price.MaxVariantPrice !== undefined
    );
};

const getActivePrice = (product: ProductSearchResult, price: ProductPrice, priceRangeFeature: boolean): number => {    
    if (showPriceRangeForMasterProduct(product, price, priceRangeFeature) && price.MinVariantPrice !== undefined) {
        return price.MinVariantPrice;
    }

    if (price.CustomerContextualPrice !== undefined) {
        return price.CustomerContextualPrice;
    }

    return getOriginalPrice(price);
};
//mmx-feature: Sale image --end

/**
 * Gets the product page url from the default swatch selected.
 * @param  coreContext - Context of the module using the component.
 * @param productUrl - Product page url for the product card.
 * @param  productData - Product card to be rendered.
 * @returns The product card image url.
 */
function getProductPageUrlFromDefaultSwatch(
    coreContext: ICoreContext,
    productUrl: string,
    productData?: ProductSearchResult
): string | undefined {
    const defaultSwatch = getDefaultSwatchSelected(coreContext, productData);
    if (!defaultSwatch?.SwatchValue) {
        return productUrl;
    }

    const siteContext = coreContext as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    const queryStringEncoded = encodeURIComponent(defaultSwatch.SwatchValue);
    const queryString = `${dimensionToPreSelectInProductCard}=${queryStringEncoded}`;
    return updateProductUrl(productUrl, coreContext, queryString);
}

const ProductCard: React.FC<IProductComponentProps> = ({
    data,
    context,
    imageSettings,
    savingsText,
    freePriceText,
    originalPriceText,
    currentPriceText,
    ratingAriaLabel,
    ratingCountAriaLabel,
    allowBack,
    typeName,
    id,
    telemetryContent,
    quickViewButton,
    productComparisonButton,
    isEnabledProductDescription,
    isPriceMinMaxEnabled,
    priceResources,
    inventoryLabel,
    dimensionAvailabilities,
    swatchItemAriaLabel
    //mmx-feature: Add to cart button --start
    , addToCartResources
    , freeShippingLabel
    , stockStatusLabel
    , displayBasicAvailabilityOnly
    //mmx-feature: Add to cart button --end

}) => {
    const product = data.product;
    const productPrice = data.productPrice;

    //mmx-feature: Add to cart button --start
    const productVariant = data.productDefaultVariant;
    const productAvailabilities = data.productAvailability;
    const rebateLines = data.productRebates;
    //mmx-feature: Add to cart button --end

    let productUrl = getProductPageUrlSync(product?.Name ?? '', product?.RecordId ?? Number.MIN_VALUE, context.actionContext, undefined);
    if (allowBack) {
        productUrl = updateProductUrl(productUrl, context, 'back=true');
    }
    const productImageUrlFromSwatch = getProductImageUrlFromDefaultSwatch(context, product) ?? product?.PrimaryImageUrl;
    const productPageUrlFromSwatch = getProductPageUrlFromDefaultSwatch(context, productUrl, product) ?? productUrl;
    const [productPageUrl, setProductPageUrl] = useState<string>(productPageUrlFromSwatch);
    const [productImageUrl, setProductImageUrl] = useState<string | undefined>(productImageUrlFromSwatch);
    React.useEffect(() => {
        setProductPageUrl(productPageUrlFromSwatch);
        setProductImageUrl(productImageUrlFromSwatch);
    }, [productUrl, productPageUrlFromSwatch, productImageUrlFromSwatch]);
    const [selectedSwatchItems] = useState(new Dictionary<DimensionTypes, ISwatchItem>());
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access -- app context is generic
    const enableStockCheck = context.app.config.enableStockCheck;

    const siteContext = context as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    /**
     * Updates the product page and Image url based on swatch selected.
     * @param coreContext - Context of the caller.
     * @param swatchItem - Dimension swatch selected.
     */
    const updatePageAndImageUrl = React.useCallback(
        (coreContext: ICoreContext, swatchItem: ISwatchItem) => {
            const dimensionType = swatchItem.dimensionType;
            selectedSwatchItems.setValue(dimensionType, swatchItem);
            if (StringExtensions.isNullOrWhitespace(swatchItem.value)) {
                return;
            }
            const queryString = `${dimensionType}=${swatchItem.value}`;
            let productPageUrlWithSwatch = '';
            if (productPageUrl.includes(dimensionType)) {
                const newUrl = new URL(productPageUrl, coreContext.request.apiSettings.baseUrl);
                newUrl.searchParams.delete(dimensionType);
                productPageUrlWithSwatch = updateProductUrl(newUrl.toString(), context, queryString);
            } else {
                productPageUrlWithSwatch = updateProductUrl(productPageUrl, context, queryString);
            }
            setProductPageUrl(productPageUrlWithSwatch);
            if (dimensionType === dimensionToPreSelectInProductCard) {
                const swatchProductImageUrl = ArrayExtensions.hasElements(swatchItem.productImageUrls)
                    ? swatchItem.productImageUrls[0]
                    : undefined;
                const newImageUrl = generateImageUrl(swatchProductImageUrl, coreContext.request.apiSettings);
                setProductImageUrl(newImageUrl);
            }
        },
        [selectedSwatchItems, context, productPageUrl]
    );

    if (!product) {
        return null;
    }

    /**
     * Checks if rendering the particular dimensions is allowed for product card.
     * @param dimensionType - Dimension to be displayed.
     * @returns Updates the state with new product page url.
     */
    function shouldDisplayDimension(dimensionType: string): boolean {
        const dimensionsContext = context as ICoreContext<IDimensionsApp>;
        const dimensionsToDisplayOnProductCard = dimensionsContext.app.config.dimensionsInProductCard;
        return (
            ArrayExtensions.hasElements(dimensionsToDisplayOnProductCard) &&
            !dimensionsToDisplayOnProductCard.includes(DimensionTypes.none) &&
            dimensionsToDisplayOnProductCard.includes(dimensionType.toLocaleLowerCase() as DimensionTypes)
        );
    }

    /**
     * Gets the react node for product dimension as swatch.
     * @param  attributeValues - Attribute value property from product.
     * @returns The node representing markup for unit of measure component.
     */
    function renderProductDimensions(attributeValues?: AttributeValue[]): JSX.Element | null {
        if (!attributeValues) {
            return null;
        }

        return (
            <div className='msc-product__dimensions'>
                {attributeValues.map((item: AttributeValue) => {
                    const dimensionTypeValue = item.KeyName?.toLocaleLowerCase() ?? '';
                    if (!shouldDisplayDimension(dimensionTypeValue)) {
                        return null;
                    }

                    const siteContext = context as ICoreContext<IDimensionsApp>;
                    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;
                    const dimensionType = dimensionTypeValue as DimensionTypes;
                    const swatches =
                        item.Swatches?.map<ISwatchItem>(swatchItem => {
                            return {
                                itemId: `${item.RecordId ?? ''}-${dimensionTypeValue}-${swatchItem.SwatchValue ?? ''}`,
                                value: swatchItem.SwatchValue ?? '',
                                dimensionType,
                                colorHexCode: swatchItem.SwatchColorHexCode,
                                imageUrl: swatchItem.SwatchImageUrl,
                                productImageUrls: swatchItem.ProductImageUrls,
                                isDefault: swatchItem.IsDefault,
                                swatchItemAriaLabel: swatchItemAriaLabel ? format(swatchItemAriaLabel, dimensionType) : '',
                                isDisabled:
                                    enableStockCheck &&
                                    dimensionAvailabilities?.find(
                                        dimensionAvailability => dimensionAvailability.value === (swatchItem.SwatchValue ?? '')
                                    )?.isDisabled
                            };
                        }) ?? [];
                    if (
                        dimensionType === dimensionToPreSelectInProductCard &&
                        ArrayExtensions.hasElements(swatches) &&
                        !swatches.some(swatch => swatch.isDefault)
                    ) {
                        swatches[0].isDefault = true;
                    }
                    return (
                        <ProductComponentSwatchComponent
                            context={context}
                            swatches={swatches}
                            onSelectDimension={updatePageAndImageUrl}
                            key={item.RecordId}
                        />
                    );
                })}
            </div>
        );
    }

    function renderQuickView(quickview: React.ReactNode, item?: number): JSX.Element | undefined {
        if (quickview === null) {
            return undefined;
        }
        const selectedDimensions: ProductDimension[] = selectedSwatchItems.getValues().map<ProductDimension>(swatches => {
            return {
                DimensionTypeValue: convertDimensionTypeToProductDimensionType(swatches.dimensionType),
                DimensionValue: {
                    RecordId: 0,
                    Value: swatches.value
                }
            };
        });
        return React.cloneElement(quickview as React.ReactElement, { selectedProductId: item, selectedDimensions });
    }

    //mmx-feature: Add to cart --start
    /**
     * Renders the add to cart button
     * @param context 
     * @param product - Product search result
     * @param productPrice - Product price
     * @param productVariant - Product variant info
     * @param productAvailabilities - Product variant availability
     * @returns React element for the specific product
     */
    function renderAddToCartButton(
        context: ICoreContext,
        product: ProductSearchResult,
        productPrice: ProductPrice | undefined,
        productVariant: SimpleProduct | undefined,
        productAvailabilities: ProductAvailableQuantity | undefined,
        quickViewButton: React.ReactNode | undefined,
        addToCartResources?: IProductComponentAddToCartResources
    ): JSX.Element | null {
        const price = productPrice ?? {
            BasePrice: product.BasePrice,
            AdjustedPrice: product.BasePrice,
            CustomerContextualPrice: product.Price,
            MaxVariantPrice: product.MaxVariantPrice ? product.MaxVariantPrice : product.Price,
            MinVariantPrice: product.MinVariantPrice ? product.MinVariantPrice : product.Price
        };

        //Add view details button
        const isInStock  = productAvailabilities ? getValueFromExtensionProperties(productAvailabilities, 'InStock')?.BooleanValue : undefined;
        //@ts-ignore
        if ((productAvailabilities?.AvailableQuantity ?? 0) <= 0 && isInStock && context.app.config.productComponentButtonBehaviour === 'quickView') {
             if (quickViewButton === null) {
                return null;
            }

            const selectedDimensions: ProductDimension[] = selectedSwatchItems.getValues().map<ProductDimension>(swatches => {
                return {
                    DimensionTypeValue: convertDimensionTypeToProductDimensionType(swatches.dimensionType),
                    DimensionValue: {
                        RecordId: 0,
                        Value: swatches.value
                    }
                };
            });
            const quickView = React.cloneElement(quickViewButton as React.ReactElement, { selectedProductId: product.RecordId, selectedDimensions, quickViewButtonClassName: 'msc-add-to-cart__view-details' });
            return (
                <div className="msc-add-to-cart__container">
                    {quickView}
                </div>
            );
        }
        return (
            <div className="msc-add-to-cart__container">
                <AddToCartComponent
                    addToCartText={addToCartResources?.addToCartText ?? ''}
                    navigationUrl={getUrlSync('cart', context.actionContext)}                
                    context={context}
                    id={`${product.RecordId}`}
                    typeName={''}
                    data={{ product: productVariant ?? product as SimpleProduct, price: price }}
                    productAvailability={productAvailabilities}
                    dialogStrings={addToCartResources}
                />
            </div>
        );
    }
    //mmx-feature: Add to cart --end

    // Construct telemetry attribute to render
    const payLoad = getPayloadObject('click', telemetryContent!, '', product.RecordId.toString());

    const attribute = getTelemetryAttributes(telemetryContent!, payLoad);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- -- Do not need type check for appsettings
    const isUnitOfMeasureEnabled = context.app.config && context.app.config.unitOfMeasureDisplayType === 'buyboxAndBrowse';

    //mmx-feature: long product names --start
    const fullName = getProductName(product, context.actionContext);
    const brand = getBrand(product);
    const brandString = brand ? brand.concat(' ') : '';
    const productName = `${brandString}${fullName}`;
    //mmx-feature: long product names --end

    //mmx-feature: mail-in rebate --start
    const mailInRebateComponent =
        rebateLines && rebateLines.length > 0
        ? (
            <MailInRebateComponent
                id={id}
                className={'msc-product__mmx-mail-in-rebate'}
                typeName={typeName}
                context={context}
                compactMode={true}
                trimPrice={true}
                linkToRebatePage={false}
                resources={{ mailInRebateHeader: 'Mail-In Rebate' }}
                data={{ productId: product.RecordId, rebateLines }}
            />
        ) : (
            undefined
        );
    //mmx-feature: mail-in rebate --end
    //mmx-feature: clearance --start
    const isClearance = product.ExtensionProperties?.find(property => property.Key === 'IsClearanceLine')?.Value?.BooleanValue;
    const clearanceComment = isClearance
        ? product.ExtensionProperties?.find(property => property.Key === 'COMMENT')?.Value?.StringValue
        : undefined;
    const clearanceWarranty = isClearance
        ? product.ExtensionProperties?.find(property => property.Key === 'Warranty')?.Value?.StringValue
        : undefined;
    const productComponentContent = isEnabledProductDescription ? (<>
    {/*
    return (
        <>
            {isEnabledProductDescription ? (
                <a
                    href={productPageUrl}
                    onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
                    aria-label={renderLabel(
                        product.Name,
                        context.cultureFormatter.formatCurrency(product.Price),
                        product.AverageRating,
                        ratingAriaLabel,
                        product.TotalRatings,
                        ratingCountAriaLabel
                    )}
                    className='msc-product'
                    {...attribute}
                >
    */}
            {/* clearance diff --start */}
            {!!isClearance && <p className='mmx-clearance__sku'>SKU: {product.ProductNumber?.split(' :')[0]}</p>}
            {!!clearanceWarranty && <p>{clearanceWarranty}</p>}
            {!!clearanceComment && <p className='mmx-clearance__comment'>{clearanceComment}</p>}
            {/* clearance diff --end */}
            <div className='msc-product__image__description'>
                <div className='msc-product__image'>
                    {renderProductPlacementImage(
                        imageSettings,
                        context.request.gridSettings,
                        productImageUrl,
                        product.PrimaryImageUrl,
                        product.Name,
                        context
                    )}
                </div>
                <div className='msc-product__title_description'>
                    {/*mmx-feature: Updated Product Cards --start
                    <h2 className='msc-product__title__text'>{product.Name}</h2>
                    */}
                    <div className='msc-product__info'>
                        <span className='msc-product__id'>{product.ProductNumber?.split(' : ')[0] ?? ''}</span>
                    </div>
                    <h2 className='msc-product__title__text'>{productName}</h2>
                    {stockStatusLabel}
                    {freeShippingLabel}
                    {/*mmx-feature: Updated Product Cards --end*/}
                    {renderPrice(
                        context,
                        typeName,
                        id,
                        product,
                        savingsText,
                        freePriceText,
                        originalPriceText,
                        currentPriceText,
                        isPriceMinMaxEnabled,
                        priceResources,
                        productPrice
                    )}
                    {isUnitOfMeasureEnabled && renderProductUnitOfMeasure(product.DefaultUnitOfMeasure)}
                    {/*mmx-feature: --start*/}
                    {displayBasicAvailabilityOnly && renderProductAvailability(inventoryLabel)}
                    {/*mmx-feature:--end*/}
                    {renderDescription(product.Description)}
                </div>
            </div>
            {/*
                </a>
            */}
            </>) : (<>
            {/*
                <a
                    href={productPageUrl}
                    onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
                    aria-label={renderLabel(
                        product.Name,
                        context.cultureFormatter.formatCurrency(product.Price),
                        product.AverageRating,
                        ratingAriaLabel
                    )}
                    className='msc-product'
                    {...attribute}
                >
            */}
            <div className='msc-product__image'>
                {renderProductPlacementImage(
                    imageSettings,
                    context.request.gridSettings,
                    productImageUrl,
                    product.PrimaryImageUrl,
                    product.Name,
                    context
                )}
            </div>
            {/* clearance diff --start */}
            {!!isClearance && <p className='mmx-clearance__sku'>SKU: {product.ProductNumber?.split(' :')[0]}</p>}
            {!!clearanceWarranty && <p>{clearanceWarranty}</p>}
            {!!clearanceComment && <p className='mmx-clearance__comment'>{clearanceComment}</p>}
            {/* clearance diff --end */}
            <div className='msc-product__details'>
                {/*mmx-feature: Long product names--start
                <h2 className='msc-product__title'>{product.Name}</h2>
                */}
                <div className='msc-product__info'>
                    <span className='msc-product__id'>{product.ProductNumber?.split(' : ')[0] ?? ''}</span>
                </div>
                <h2 className='msc-product__title'>{productName}</h2>
                {stockStatusLabel}
                {freeShippingLabel}
                {/*mmx-feature: Long product names--end*/}
                {isUnitOfMeasureEnabled && renderProductUnitOfMeasure(product.DefaultUnitOfMeasure)}
                {displayBasicAvailabilityOnly && renderProductAvailability(inventoryLabel)}
            </div>
            {/*
                </a>
            )}
            {renderProductDimensions(product.AttributeValues)}
            {!context.app.config.hideRating &&
                renderRating(context, typeName, id, product.AverageRating, product.TotalRatings, ratingAriaLabel, ratingCountAriaLabel)}
            {renderProductAvailability(inventoryLabel)}
            {quickViewButton && renderQuickView(quickViewButton, product.RecordId)}
            {productComparisonButton && renderProductComparisonButton(productComparisonButton, product, getCatalogId(context.request))}
            </>
    );
    */}
    </>);

    return isClearance ? (
        <>
            <a
                href={getUrlSync(`clearance-item/${product.RecordId}--${getValueFromExtensionProperties(product, 'CLEARANCEID')?.LongValue?.toString()}`, context.actionContext)}
                aria-label={renderLabel(
                    product.Name,
                    context.cultureFormatter.formatCurrency(product.Price),
                    product.AverageRating,
                    ratingAriaLabel,
                    isEnabledProductDescription ? product.TotalRatings : undefined,
                    isEnabledProductDescription ? ratingCountAriaLabel : undefined
                )}
                className='msc-product'
                {...attribute}
            >
                {productComponentContent}
            </a>
            {!isEnabledProductDescription && <>
                {renderProductDimensions(product.AttributeValues)}
                {!context.app.config.hideRating &&
                renderRating(context, typeName, id, product.AverageRating, product.TotalRatings, ratingAriaLabel, ratingCountAriaLabel)}
                {/*
                //mmx-feature: Inventory attribute --start
                {renderProductAvailability(inventoryLabel)}
                //mmx-feature: Inventory attribute --end
                */}
                {
                //mmx-feature: Sale image --start
                renderProductSaleImage(context, product, productPrice, isPriceMinMaxEnabled)
                //mmx-feature: Sale image --end
                }
                {quickViewButton && renderQuickView(quickViewButton, product.RecordId)}
                {productComparisonButton && renderProductComparisonButton(productComparisonButton, product, getCatalogId(context.request))}
            </>}
        </>
    ) : (
        <>
            <a
                href={productPageUrl}
                onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
                aria-label={renderLabel(
                    product.Name,
                    context.cultureFormatter.formatCurrency(product.Price),
                    product.AverageRating,
                    ratingAriaLabel,
                    isEnabledProductDescription ? product.TotalRatings : undefined,
                    isEnabledProductDescription ? ratingCountAriaLabel : undefined
                )}
                className='msc-product'
                {...attribute}
            >
                {productComponentContent}
            </a>
            {!isEnabledProductDescription && <>
                {renderProductDimensions(product.AttributeValues)}
                {!context.app.config.hideRating &&
                renderRating(context, typeName, id, product.AverageRating, product.TotalRatings, ratingAriaLabel, ratingCountAriaLabel)}
                {/*
                //mmx-feature: Inventory attribute display --start
                {renderProductAvailability(inventoryLabel)}
                //mmx-feature: Inventory attribute display --end
                */}
                {
                //mmx-feature: Sale image and mail in rebate --start
                <div className={'msc-product__sales-banner'}>
                    {renderProductSaleImage(context, product, productPrice, isPriceMinMaxEnabled)}
                    {mailInRebateComponent}
                </div>
                //mmx-feature: Sale image and mail in rebate  --end
                }
                {quickViewButton && renderQuickView(quickViewButton, product.RecordId)}
                {/* mmmx-feature: availability --start */}
                <div className='msc-product__price-and-availability'>
                    {/* mmx-feature: Sale image --start */}
                    {renderProductAvailabilityContainer(productAvailabilities, displayBasicAvailabilityOnly, addToCartResources)}
                    {/* mmx-feature: Sale image --end */}
                    {renderPrice(
                        context,
                        typeName,
                        id,
                        product,
                        savingsText,
                        freePriceText,
                        originalPriceText,
                        currentPriceText,
                        isPriceMinMaxEnabled,
                        priceResources,
                        productPrice
                    )}
                </div>
                {/* mmmx-feature: availability --end */}
                {renderAddToCartButton(context, product, productPrice, productVariant, productAvailabilities, quickViewButton, addToCartResources)}
                {productComparisonButton && renderProductComparisonButton(productComparisonButton, product, getCatalogId(context.request))}
            </>}
        </>
    );
    //mmx-feature: clearance --end
};

//mmx-feature: Inventory attribute display --start
function renderProductAvailabilityContainer(
    productAvailabilities?: ProductAvailableQuantity,
    displayBasicAvailabilityOnly?: boolean,
    addToCartResources?: IProductComponentAddToCartResources
): JSX.Element {
    let onlineStockLabel = productAvailabilities
        ? getStockLabel(
              productAvailabilities?.AvailableQuantity,
              addToCartResources?.onlineProductStockLabel,
              addToCartResources?.onlineInStockText,
              addToCartResources?.onlineOutOfStockText
          )
        : undefined;

    const productStoreStock = productAvailabilities
        ? getValueFromExtensionProperties(productAvailabilities, 'LocationInventory')?.DecimalValue
        : undefined;
    const storeName = productAvailabilities
        ? getValueFromExtensionProperties(productAvailabilities, 'LocationName')?.StringValue
        : undefined;
    let inStoreStockLabel = productAvailabilities
        ? getStockLabel(
              productStoreStock,
              addToCartResources?.inStoreProductStockLabel?.replace('{1}', storeName ?? ''),
              addToCartResources?.inStoreInStockText,
              addToCartResources?.inStoreOutOfStockText
          )
        : undefined;

    return (
        <div className={'msc-product__availability-container'}>
            {!displayBasicAvailabilityOnly && onlineStockLabel}
            {!displayBasicAvailabilityOnly && storeName && inStoreStockLabel}
        </div>
    );
}

function getStockLabel(
    availableQuantity: number = 0,
    stockLabel?: string,
    inStockText?: string,
    outOfStockText?: string
): JSX.Element | undefined {
    if (stockLabel) {
        if (availableQuantity > 0) {
            const label = stockLabel.replace('{0}', inStockText ?? '');
            return (
                <div className={'msc-product__stock-label'} aria-label={label}>
                    <span className={'mmx-in-stock-symbol'}></span>
                    {label}
                </div>
            );
        } else {
            const label = stockLabel.replace('{0}', outOfStockText ?? '');
            return (
                <div className={'msc-product__stock-label'} aria-label={label}>
                    <span className={'mmx-out-of-stock-symbol'}></span>
                    {label}
                </div>
            );
        }
    }
    return;
}
//mmx-feature: Inventory attribute display --end

function renderLabel(
    name?: string,
    price?: string,
    rating?: number,
    ratingAriaLabelText?: string,
    reviewCount?: number,
    ratingCountAriaLabelText?: string
): string {
    const reviewCountArialableText = getReviewAriaLabel(reviewCount, ratingCountAriaLabelText ?? '');
    return `${name ?? ''} ${price ?? ''} ${getRatingAriaLabel(rating, ratingAriaLabelText)}${
        reviewCountArialableText ? ` ${reviewCountArialableText}` : ''
    }`;
}

function renderDescription(description?: string): JSX.Element | null {
    return <p className='msc-product__text'>{description}</p>;
}

function getRatingAriaLabel(rating?: number, ratingAriaLabel?: string): string {
    if (rating && ratingAriaLabel) {
        const roundedRating = rating.toFixed(2);
        return format(ratingAriaLabel || '', roundedRating, '5');
    }
    return '';
}

function getReviewAriaLabel(reviewCount?: number, ratingCountAriaLabelText?: string): string {
    if (reviewCount && ratingCountAriaLabelText) {
        return format(ratingCountAriaLabelText || '', reviewCount);
    }
    return '';
}

function renderRating(
    coreContext: ICoreContext,
    moduleTypeName: string,
    moduleId: string,
    avgRating?: number,
    totalRatings?: number,
    ariaLabel?: string,
    ratingCountAriaLabel?: string
): JSX.Element | null {
    if (!avgRating) {
        return null;
    }

    const numberRatings = totalRatings?.toString() || undefined;
    const ratingAriaLabelText = getRatingAriaLabel(avgRating, ariaLabel);
    const ratingCountAriaLabelText = getReviewAriaLabel(Number(numberRatings), ratingCountAriaLabel);

    return (
        <RatingComponent
            context={coreContext}
            id={moduleId}
            typeName={moduleTypeName}
            avgRating={avgRating}
            ratingCount={numberRatings}
            readOnly
            ariaLabel={ratingAriaLabelText}
            ratingCountAriaLabel={ratingCountAriaLabelText}
            data={{}}
        />
    );
}

function renderPrice(
    context: ICoreContext,
    typeName: string,
    id: string,
    productSearchResult: ProductSearchResult,
    savingsText?: string,
    freePriceText?: string,
    originalPriceText?: string,
    currentPriceText?: string,
    isPriceMinMaxEnabled?: boolean,
    priceResources?: IPriceComponentResources,
    productPrice?: ProductPrice
): JSX.Element | null {
    const product: SimpleProduct = {
        // This is a fake product. The product type is only used for price component to determine if it should show price range or not.
        ProductTypeValue: productSearchResult.IsMasterProduct ? ProductType.Master : ProductType.Standalone
    } as SimpleProduct;

    const price: ProductPrice = productPrice ?? {
        BasePrice: productSearchResult.BasePrice,
        //mmx-feature: Strikethrough pricing --start
        /*
        AdjustedPrice: productSearchResult.Price,
        */
        AdjustedPrice: productSearchResult.BasePrice,
        //mmx-feature: Strikethrough pricing --end
        CustomerContextualPrice: productSearchResult.Price,
        MaxVariantPrice: productSearchResult.MaxVariantPrice ? productSearchResult.MaxVariantPrice : productSearchResult.Price,
        MinVariantPrice: productSearchResult.MinVariantPrice ? productSearchResult.MinVariantPrice : productSearchResult.Price
    };

    return (
        <PriceComponent
            context={context}
            id={id}
            typeName={typeName}
            product={product}
            data={{ price }}
            savingsText={savingsText}
            freePriceText={freePriceText}
            originalPriceText={originalPriceText}
            isPriceMinMaxEnabled={isPriceMinMaxEnabled}
            priceResources={priceResources}
        />
    );
}

function renderProductPlacementImage(
    imageSettings?: IImageSettings,
    gridSettings?: IGridSettings,
    imageUrl?: string,
    fallbackImageUrl?: string,
    altText?: string,
    context?: ICoreContext<IGeneric<IAny>>
): JSX.Element | null {
    if (!imageUrl || !gridSettings || !imageSettings) {
        //mmx-feature --start
        /*
        return null;
        */
        return (<Image
                alt={''}
                altText={''}
                requestContext={context?.actionContext.requestContext}
                src={''}
                fallBackSrc={''}
                gridSettings={context?.request.gridSettings!}
                loadFailureBehavior='empty'
                bypassHideOnFailure
        />);
        //mmx-feature --end
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- Site level config can be of any type.
    const contextConfig = context?.app.config?.placeholderImageName;
    const emptyPlaceHolderImage = contextConfig as string;
    let fallbackImageSource = fallbackImageUrl;
    if (emptyPlaceHolderImage && fallbackImageUrl) {
        fallbackImageSource = `${fallbackImageUrl},${emptyPlaceHolderImage}`;
    }
    const img: IImageData = {
        src: imageUrl,
        altText: altText ? altText : '',
        fallBackSrc: fallbackImageSource
    };
    const imageProps = {
        gridSettings,
        imageSettings
    };
    imageProps.imageSettings.cropFocalRegion = true;
    return (
        <Image
            requestContext={context?.actionContext.requestContext}
            {...img}
            {...imageProps}
            loadFailureBehavior='empty'
            bypassHideOnFailure
        />
    );
}

export const ProductComponent: React.FunctionComponent<IProductComponentProps> = msdyn365Commerce.createComponentOverride<
    IProductComponent
>('Product', { component: ProductCard, ...PriceComponentActions });

export default ProductComponent;
